import { FiltersV2StoreContextProvider } from "PFApp/components/filters_v2";
import { ResourceTableToggleProps } from "PFApp/components/resource_table_toggle/resource_table_toggle";
import { Stack } from "PFComponents/containers";
import { MultiToggle, Option } from "PFComponents/multi_toggle";
import { useEffect, useState } from "react";

import { MatchesPanel } from "./components/matches_panel";
import { NamedResourcePanel } from "./components/named_resource_panel";
import { ShortlistsPanel } from "./components/shortlists_panel";
import { ResourceTableOption } from "./resource_table_option";

export type ResourceTableToggleContentProps = Omit<ResourceTableToggleProps, "availableOptions"> & {
  visibleOptions: Option<ResourceTableOption>[];
};

export const ResourceTableToggleContent = ({
  visibleOptions,
  selectedOption: controlledSelectedOption,
  defaultSelectedOption,
  onSelectedOptionChange,
  resetSelectionOnOptionChange = true,
  onRowSelectionChange,
  rowSelectionState,
  pageKey,
  matchesOptions,
  shortlistsOptions,
  renderActionCell
}: ResourceTableToggleContentProps) => {
  const [uncontrolledSelected, setUncontrolledSelected] = useState<ResourceTableOption>(() => {
    if (defaultSelectedOption && visibleOptions.some(({ id }) => id === defaultSelectedOption)) {
      return defaultSelectedOption;
    }
    return visibleOptions[0].id;
  });

  const isControlled = controlledSelectedOption !== undefined;
  const rawSelectedOption = isControlled ? controlledSelectedOption : uncontrolledSelected;

  const selectedOption = !visibleOptions.some(({ id }) => id === rawSelectedOption)
    ? visibleOptions[0].id
    : rawSelectedOption;

  useEffect(() => {
    if (selectedOption !== rawSelectedOption) {
      onSelectedOptionChange?.(selectedOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (resetSelectionOnOptionChange) {
      onRowSelectionChange?.({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  const handleSelectionChange = (next: ResourceTableOption) => {
    if (!isControlled) {
      setUncontrolledSelected(next);
    }
    onSelectedOptionChange?.(next);
  };

  const isNamedResourceVisible = visibleOptions.some(({ id }) => id === ResourceTableOption.NamedResource);
  const handleNamedResourceFallback = isNamedResourceVisible
    ? () => handleSelectionChange(ResourceTableOption.NamedResource)
    : undefined;

  return (
    <Stack gap="spacingLg">
      {visibleOptions.length > 1 && (
        <MultiToggle<ResourceTableOption>
          controlledValue={selectedOption}
          onChange={handleSelectionChange}
          options={visibleOptions}
        />
      )}

      {selectedOption === ResourceTableOption.NamedResource && (
        <NamedResourcePanel
          pageKey={pageKey}
          onRowSelectionChange={onRowSelectionChange}
          rowSelectionState={rowSelectionState}
          renderActionCell={renderActionCell}
        />
      )}

      {selectedOption === ResourceTableOption.Matches && matchesOptions && (
        <FiltersV2StoreContextProvider name={matchesOptions.matchesFiltersStorePage}>
          <MatchesPanel
            excludedProfileIds={matchesOptions.excludedProfileIds}
            activity={matchesOptions.activity}
            pageKey={pageKey}
            matchesFiltersStorePage={matchesOptions.matchesFiltersStorePage}
            renderNoMatchesActionNamedResourceFallback={
              matchesOptions.renderNoMatchesActionNamedResourceFallback
            }
            onRowSelectionChange={onRowSelectionChange}
            rowSelectionState={rowSelectionState}
            onNamedResourceFallbackClick={handleNamedResourceFallback}
            renderActionCell={renderActionCell}
          />
        </FiltersV2StoreContextProvider>
      )}

      {selectedOption === ResourceTableOption.Shortlists && shortlistsOptions && (
        <FiltersV2StoreContextProvider name={shortlistsOptions.shortlistsFiltersStorePage}>
          <ShortlistsPanel
            activity={shortlistsOptions.activity}
            pageKey={pageKey}
            onRowSelectionChange={onRowSelectionChange}
            rowSelectionState={rowSelectionState}
            renderActionCell={renderActionCell}
            onShortlistUndo={shortlistsOptions.onShortlistUndo}
          />
        </FiltersV2StoreContextProvider>
      )}
    </Stack>
  );
};
